import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { ROLE_OPTIONS, roleLabel, SECTIONS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserGear, Plus, Trash, PencilSimple, Key, CheckCircle, Ticket, Copy, ToggleLeft, ToggleRight } from "@phosphor-icons/react";

const defaultPerms = () => Object.fromEntries(SECTIONS.map((s) => [s.to, { view: true, edit: true, delete: false }]));
const emptyForm = { name: "", email: "", password: "", role: "gerente_area", contractor_id: "", can_log_bitacora: false, can_delete: false, can_download: false, allowed_sections: [], phone: "", permissions: defaultPerms() };

export default function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const [codes, setCodes] = useState([]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeForm, setCodeForm] = useState({ code: "", role: "gerente_area", contractor_id: "", max_uses: "", email: "" });
  const [templates, setTemplates] = useState([]);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  const loadCodes = () => api.get("/invite-codes").then((r) => setCodes(r.data)).catch(() => {});
  const loadTemplates = () => api.get("/permission-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); loadCodes(); loadTemplates(); api.get("/contractors").then((r) => setContractors(r.data)); }, []);

  const cmap = Object.fromEntries(contractors.map((c) => [c.id, c]));

  const openNew = () => { setForm({ ...emptyForm, permissions: defaultPerms() }); setEditId(null); setOpen(true); };
  const buildPerms = (u) => {
    if (u.permissions && Object.keys(u.permissions).length) {
      return Object.fromEntries(SECTIONS.map((s) => [s.to, { view: !!u.permissions[s.to]?.view, edit: !!u.permissions[s.to]?.edit, delete: !!u.permissions[s.to]?.delete }]));
    }
    const secs = u.allowed_sections;
    const seesAll = !Array.isArray(secs) || secs.length === 0 || secs.includes("all");
    return Object.fromEntries(SECTIONS.map((s) => { const v = seesAll || secs.includes(s.to); return [s.to, { view: v, edit: v, delete: !!u.can_delete && v }]; }));
  };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role, contractor_id: u.contractor_id || "", can_log_bitacora: !!u.can_log_bitacora, can_delete: !!u.can_delete, can_download: !!u.can_download, allowed_sections: u.allowed_sections || [], phone: u.phone || "", permissions: buildPerms(u) }); setEditId(u.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/users/${editId}`, { name: form.name, role: form.role, contractor_id: form.contractor_id || null, can_log_bitacora: form.can_log_bitacora, can_delete: form.can_delete, can_download: form.can_download, allowed_sections: form.allowed_sections, phone: form.phone || null, permissions: form.permissions });
      } else {
        await api.post("/users", { ...form, contractor_id: form.contractor_id || null, phone: form.phone || null });
      }
      toast.success("Usuario guardado"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success("Contraseña actualizada"); setResetFor(null); setResetPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => { if (!window.confirm("¿Eliminar usuario?")) return; try { await api.delete(`/users/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const getPerm = (sec, act) => !!form.permissions?.[sec]?.[act];
  const togglePerm = (sec, act, val) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [sec]: { ...(f.permissions?.[sec] || {}), [act]: val } } }));
  const setColumn = (act, val) => setForm((f) => { const p = { ...f.permissions }; SECTIONS.forEach((s) => { p[s.to] = { ...(p[s.to] || {}), [act]: val }; }); return { ...f, permissions: p }; });
  const grantEverything = () => setForm((f) => ({ ...f, can_delete: true, permissions: Object.fromEntries(SECTIONS.map((s) => [s.to, { view: true, edit: true, delete: true }])) }));
  const clearEverything = () => setForm((f) => ({ ...f, permissions: Object.fromEntries(SECTIONS.map((s) => [s.to, { view: false, edit: false, delete: false }])) }));
  const applyTemplate = (t) => { if (!t) return; setForm((f) => ({ ...f, permissions: Object.fromEntries(SECTIONS.map((s) => [s.to, { view: !!t.permissions?.[s.to]?.view, edit: !!t.permissions?.[s.to]?.edit, delete: !!t.permissions?.[s.to]?.delete }])) })); toast.success(`Plantilla "${t.name}" aplicada`); };
  const saveTemplate = async () => { const name = window.prompt("Nombre de la plantilla (ej. Supervisor, Seguridad)"); if (!name) return; try { await api.post("/permission-templates", { name, permissions: form.permissions }); toast.success("Plantilla guardada"); loadTemplates(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const deleteTemplate = async (t) => { if (!window.confirm(`¿Eliminar plantilla "${t.name}"?`)) return; try { await api.delete(`/permission-templates/${t.id}`); loadTemplates(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const saveCode = async (e) => {
    e.preventDefault();
    try {
      const payload = { code: codeForm.code || null, role: codeForm.role, contractor_id: codeForm.contractor_id || null, max_uses: codeForm.max_uses ? Number(codeForm.max_uses) : null, email: codeForm.email || null };
      await api.post("/invite-codes", payload);
      toast.success(codeForm.email ? "Código creado y enviado por correo" : "Código creado"); setCodeOpen(false); setCodeForm({ code: "", role: "gerente_area", contractor_id: "", max_uses: "", email: "" }); loadCodes();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleCode = async (c) => { try { await api.put(`/invite-codes/${c.id}/toggle`); loadCodes(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const removeCode = async (c) => { if (!window.confirm("¿Eliminar este código?")) return; try { await api.delete(`/invite-codes/${c.id}`); loadCodes(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`Código ${code} copiado`); };

  return (
    <div>
      <PageHeader overline="Equipo y roles" title="Usuarios"
        action={<PrimaryButton onClick={openNew} data-testid="add-user-btn"><Plus size={18} weight="bold" /> Nuevo usuario</PrimaryButton>} />

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
        Alta de responsables del flujo (<b className="text-foreground">Gerente de Área → Superintendente → Gerente Administrativo → Gerente General</b>). <b className="text-foreground">Todos pueden alimentar la app</b> (agregar planos, documentos, estimaciones, etc.). Marca <b className="text-foreground">"Puede borrar o eliminar cualquier cosa"</b> para autorizar a ese usuario a eliminar; si se deja apagado, no podrá borrar nada.
      </Card>

      {items.length === 0 ? (
        <EmptyState icon={UserGear} title="Sin usuarios" subtitle="Crea las cuentas de tu equipo con su rol." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Nombre", "Correo", "Rol", "Contratista", "Bitácora", "Puede borrar", ""].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors duration-150">
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-semibold bg-orange-50 text-orange-700">{roleLabel(u.role)}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{u.role === "contratista" ? (cmap[u.contractor_id]?.name || "—") : "—"}</td>
                  <td className="px-4 py-3">{(u.can_log_bitacora || ["admin", "supervisor", "contratista"].includes(u.role)) ? <CheckCircle size={18} weight="fill" className="text-success" /> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3" data-testid={`user-candelete-${u.id}`}>{(u.can_delete || ["admin", "gerente_general"].includes(u.role)) ? <CheckCircle size={18} weight="fill" className="text-success" /> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => { setResetFor(u); setResetPw(""); }} data-testid={`reset-pw-${u.id}`} title="Resetear contraseña" className="p-1.5 text-muted-foreground hover:text-primary"><Key size={18} /></button>
                    <button onClick={() => openEdit(u)} className="p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                    {u.id !== user.id && <button onClick={() => remove(u.id)} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar usuario" : "Nuevo usuario"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nombre *"><input data-testid="user-name" required className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          {!editId && (
            <>
              <Field label="Correo *"><input data-testid="user-email" type="email" required className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Contraseña *"><input data-testid="user-password" type="text" required className={inputClass} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            </>
          )}
          <Field label="Rol *">
            <select data-testid="user-role" className={inputClass} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Teléfono (WhatsApp, opcional)"><input data-testid="user-phone" className={inputClass} value={form.phone} placeholder="Ej. +52 55 1234 5678" onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          {form.role === "contratista" && (
            <Field label="Vincular a contratista">
              <select className={inputClass} value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
                <option value="">— Ninguno —</option>
                {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" data-testid="user-can-log" checked={form.can_log_bitacora} onChange={(e) => setForm({ ...form, can_log_bitacora: e.target.checked })} className="w-4 h-4 accent-primary" />
            Puede alimentar la bitácora de obra
          </label>
          <label className="flex items-start gap-2 text-sm font-medium cursor-pointer p-3 rounded-sm border border-input bg-muted/40">
            <input type="checkbox" data-testid="user-can-delete" checked={form.can_delete} onChange={(e) => setForm({ ...form, can_delete: e.target.checked })} className="w-4 h-4 accent-primary mt-0.5" />
            <span>Puede borrar o eliminar cualquier cosa<br /><span className="text-xs font-normal text-muted-foreground">Si se deja apagado, este usuario podrá agregar/alimentar pero NO podrá borrar nada.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm font-medium cursor-pointer p-3 rounded-sm border border-input bg-muted/40">
            <input type="checkbox" data-testid="user-can-download" checked={form.can_download} onChange={(e) => setForm({ ...form, can_download: e.target.checked })} className="w-4 h-4 accent-primary mt-0.5" />
            <span>Puede abrir y descargar documentos<br /><span className="text-xs font-normal text-muted-foreground">Por defecto solo el admin y el Director General pueden abrir/descargar documentos (PDF, planos, informes). Actívalo temporalmente para autorizar a este usuario y apágalo al terminar la tarea.</span></span>
          </label>

          <div className="border border-input rounded-sm p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold">Permisos por herramienta</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={grantEverything} data-testid="perm-grant-all" className="text-xs font-semibold text-primary hover:underline">Director General (todo)</button>
                <span className="text-muted-foreground">·</span>
                <button type="button" onClick={clearEverything} data-testid="perm-clear-all" className="text-xs font-semibold text-muted-foreground hover:underline">Quitar todo</button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap border-t border-border pt-2">
              <select data-testid="apply-template" className="h-8 text-xs rounded-sm border border-input px-2 bg-white" defaultValue="" onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); applyTemplate(t); e.target.value = ""; }}>
                <option value="">Aplicar plantilla…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button type="button" onClick={saveTemplate} data-testid="save-template-btn" className="text-xs font-semibold text-primary hover:underline">Guardar actual como plantilla</button>
              {templates.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 text-[10px] bg-muted rounded-sm px-1.5 py-0.5">{t.name}<button type="button" data-testid={`del-template-${t.id}`} onClick={() => deleteTemplate(t)} className="text-muted-foreground hover:text-danger font-bold">×</button></span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Marca qué puede hacer este usuario en cada herramienta. <b>Ver</b> = entrar y consultar · <b>Modificar</b> = crear/editar · <b>Eliminar</b> = borrar. El administrador y el Gerente General siempre tienen acceso total.</p>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left border-b border-border">
                    <th className="py-1.5 pr-2 font-semibold">Herramienta</th>
                    {[["view", "Ver"], ["edit", "Modificar"], ["delete", "Eliminar"]].map(([a, l]) => (
                      <th key={a} className="py-1.5 px-1 text-center font-semibold w-20">
                        <div>{l}</div>
                        <button type="button" onClick={() => setColumn(a, true)} data-testid={`perm-col-${a}`} className="text-[10px] text-primary hover:underline">marcar todo</button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SECTIONS.map((s) => (
                    <tr key={s.to} className="border-b border-border/60">
                      <td className="py-1.5 pr-2">{s.label}</td>
                      {["view", "edit", "delete"].map((a) => (
                        <td key={a} className="py-1.5 px-1 text-center">
                          <input type="checkbox" data-testid={`perm-${a}-${s.to}`} checked={getPerm(s.to, a)} onChange={(e) => togglePerm(s.to, a, e.target.checked)} className="w-4 h-4 accent-primary" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-user-btn">{busy ? "Guardando…" : "Guardar"}</PrimaryButton>
        </form>
      </Modal>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline flex items-center gap-2"><Ticket size={16} weight="bold" /> Registro por rol</div>
            <h3 className="font-heading font-bold text-lg">Códigos de invitación</h3>
            <p className="text-sm text-muted-foreground">Comparte un código con tu equipo. Al registrarse con él, se les asigna el rol automáticamente (sin código entran como contratista).</p>
          </div>
          <PrimaryButton onClick={() => setCodeOpen(true)} data-testid="add-invite-code-btn"><Plus size={18} weight="bold" /> Nuevo código</PrimaryButton>
        </div>
        {codes.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">Aún no hay códigos de invitación. Crea uno para dar de alta gerentes, supervisores o seguridad sin configurar su rol manualmente.</Card>
        ) : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Código", "Rol asignado", "Usos", "Estado", ""].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} data-testid={`invite-row-${c.code}`} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors duration-150">
                    <td className="px-4 py-3">
                      <button onClick={() => copyCode(c.code)} data-testid={`copy-code-${c.code}`} className="inline-flex items-center gap-2 font-mono font-bold text-foreground hover:text-primary">
                        {c.code} <Copy size={14} />
                      </button>
                    </td>
                    <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-semibold bg-orange-50 text-orange-700">{roleLabel(c.role)}</span></td>
                    <td className="px-4 py-3 text-muted-foreground">{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleCode(c)} data-testid={`toggle-code-${c.code}`} className={`inline-flex items-center gap-1 text-xs font-semibold ${c.active ? "text-success" : "text-muted-foreground"}`}>
                        {c.active ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />} {c.active ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeCode(c)} data-testid={`delete-code-${c.code}`} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <Modal open={codeOpen} onClose={() => setCodeOpen(false)} title="Nuevo código de invitación">
        <form onSubmit={saveCode} className="space-y-4">
          <Field label="Rol que asignará *">
            <select data-testid="code-role" className={inputClass} value={codeForm.role} onChange={(e) => setCodeForm({ ...codeForm, role: e.target.value })}>
              {ROLE_OPTIONS.filter((r) => r.value !== "admin").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Código (opcional)">
            <input data-testid="code-value" className={`${inputClass} uppercase`} value={codeForm.code} placeholder="Se genera automáticamente si lo dejas vacío" onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value })} />
          </Field>
          {codeForm.role === "contratista" && (
            <Field label="Vincular a contratista (opcional)">
              <select className={inputClass} value={codeForm.contractor_id} onChange={(e) => setCodeForm({ ...codeForm, contractor_id: e.target.value })}>
                <option value="">— Ninguno —</option>
                {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Máximo de usos (opcional)">
            <input data-testid="code-max-uses" type="number" min="1" className={inputClass} value={codeForm.max_uses} placeholder="Ilimitado" onChange={(e) => setCodeForm({ ...codeForm, max_uses: e.target.value })} />
          </Field>
          <Field label="Enviar por correo a (opcional)">
            <input data-testid="code-email" type="email" className={inputClass} value={codeForm.email} placeholder="correo@equipo.com — le llega la invitación" onChange={(e) => setCodeForm({ ...codeForm, email: e.target.value })} />
          </Field>
          <PrimaryButton type="submit" data-testid="save-code-btn">Crear código</PrimaryButton>
        </form>
      </Modal>

      <Modal open={!!resetFor} onClose={() => setResetFor(null)} title={`Resetear contraseña · ${resetFor?.name || ""}`}>
        <form onSubmit={doReset} className="space-y-4">
          <Field label="Nueva contraseña *"><input data-testid="reset-pw-input" type="text" required minLength={6} className={inputClass} value={resetPw} onChange={(e) => setResetPw(e.target.value)} /></Field>
          <PrimaryButton type="submit" data-testid="confirm-reset-btn">Actualizar contraseña</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
